import axios from 'axios';

const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;
const BASE_URL = process.env.REACT_APP_REPORTS_BASE_URL;
const BACKEND_URL = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

/** Pull report id from game-server responses (shape varies by endpoint). */
export function extractReportIdFromResponse(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  const candidates = [
    payload.id,
    payload.reportId,
    payload.report_id,
    payload.reportid,
    payload.data?.id,
    payload.data?.reportId,
    payload.data?.report_id,
    payload.data?.reportid,
    payload.data?.data?.id,
    payload.data?.data?.reportId,
    payload.data?.data?.report_id,
    payload.result?.id,
    payload.result?.reportId,
    payload.result?.report_id,
    payload.result?.data?.id,
    payload.result?.data?.reportId,
    payload.result?.data?.report_id,
    payload.report?.id,
    payload.report?.reportId,
    payload.report?.report_id,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim();
  }
  return null;
}

/**
 * Registered `game-server/report/*` calls use `Authorization: Bearer <token>`.
 * Org users may have no `user.token` but always have `sessionId&organizationId`.
 */
export function resolveReportsAuthToken(userToken, adminToken) {
  if (userToken != null && String(userToken).trim() !== '') return String(userToken).trim();
  if (adminToken != null && String(adminToken).trim() !== '') return String(adminToken).trim();
  return null;
}

/** Seconds for report API: number, or "MM:SS" / "HH:MM:SS". */
export function normalizeReportTimeValue(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.floor(raw));
  const s = String(raw).trim();
  const parts = s.split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

/** Cumulative report time when `previousTime` + `currentTime` are supplied. */
export function resolveReportTimeSeconds(data) {
  if (data == null || typeof data !== 'object') return 0;
  const hasPrev = data.previousTime != null && data.previousTime !== '';
  const hasCurr = data.currentTime != null && data.currentTime !== '';
  if (hasPrev || hasCurr) {
    return (
      normalizeReportTimeValue(data.previousTime) + normalizeReportTimeValue(data.currentTime)
    );
  }
  return normalizeReportTimeValue(data.time);
}

/** Guest report APIs expect `time` as HH:MM:SS string. */
function guestTimeStringForApi(timeRaw) {
  const sec = normalizeReportTimeValue(timeRaw);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function isGuestUserType(data) {
  if (!data || typeof data !== 'object') return false;
  const r = String(data.role ?? '').trim().toUpperCase();
  const ut = String(data.user_type ?? data.userType ?? '').trim().toUpperCase();
  return r === 'GUEST_USER' || ut === 'GUEST_USER';
}

function readReportPoints(report) {
  if (!report) return 0;
  const total = Number(report.total_score);
  const points = Number(report.points);
  if (Number.isFinite(total) && total >= 0) return Math.floor(total);
  if (Number.isFinite(points) && points >= 0) return Math.floor(points);
  return 0;
}

async function fetchExistingReportSnapshot({ userId, token, gameId = null }) {
  try {
    if (!BACKEND_URL || !userId || !token) {
      return { reportId: null, previousPoints: 0 };
    }
    const resp = await fetch(`${BACKEND_URL}/fetchReport`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, ...(gameId ? { gameId } : {}) }),
    });
    if (!resp.ok) return { reportId: null, previousPoints: 0 };
    const payload = await resp.json().catch(() => ({}));
    const report = payload?.report || null;
    const rid =
      report?.reportId ??
      report?.reportid ??
      report?.report_id ??
      report?.id ??
      extractReportIdFromResponse(payload);
    const reportId = rid != null && String(rid).trim() !== '' ? String(rid).trim() : null;
    return {
      reportId,
      previousPoints: readReportPoints(report),
    };
  } catch {
    return { reportId: null, previousPoints: 0 };
  }
}


export async function refreshToken(currentToken) {
  const url = `${BASE_URL}/auth/rf-token`;
  const headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
    'Idempotency-Key': uuid4(),
  };
  const response = await axios.get(url, { headers });
  return response.data.token;
}

/**
 * The fields the report service needs to CREATE a player's entry (everything that identifies the player and
 * the session). The entry is created at login; this is also what the completion step must send if that
 * login entry was missed, otherwise it would create an incomplete one (no sessionId / organizationId /
 * gameId / role). Not sent when updating an existing entry (reportId known).
 */
export function buildReportIdentity(user) {
  const u = user && typeof user === 'object' ? user : {};
  const roleUpper = String(u.role || u.source || '').trim().toUpperCase() || 'ORG_USER';
  return {
    gameId: u.gameId,
    sessionId: u.sessionId,
    organizationId: u.organizationId,
    role: roleUpper,
    user_type: roleUpper,
    owner: roleUpper,
    guestScoreMinimal: roleUpper === 'GUEST_USER',
  };
}

export async function sendReport(data) {
  const { sessionId, role, token } = data;
  if (role === 'demobypass') return true;

  // If reportId is missing but a report row already exists for this userId,
  // reuse it so we UPDATE instead of INSERT on re-join. Also accumulate points.
  const hasRid = data.reportId != null && String(data.reportId).trim() !== '';
  const adminToken =
    data.sessionId && data.organizationId ? `${data.sessionId}&${data.organizationId}` : null;
  const reportFetchToken = adminToken || token || null;
  const currentPoints = Math.max(0, Math.floor(Number(data.points) || 0));

  if (data.userId && reportFetchToken) {
    const snapshot = await fetchExistingReportSnapshot({
      userId: data.userId,
      token: reportFetchToken,
      gameId: data.gameId || null,
    });

    if (!hasRid && snapshot.reportId) {
      data = { ...data, reportId: snapshot.reportId };
    }

    if (data.accumulatePoints !== false && currentPoints > 0) {
      data = { ...data, points: snapshot.previousPoints + currentPoints };
    }
  }

  const isGuest = isGuestUserType(data);
  const reportTimeSeconds = resolveReportTimeSeconds(data);
  if (isGuest) {
    const timeStr = guestTimeStringForApi(reportTimeSeconds);
    const hasIncomingRid = data.reportId != null && String(data.reportId).trim() !== '';
    const rid = hasIncomingRid ? String(data.reportId).trim() : '';
    const guestAddPayload = data.guestScoreMinimal === true
      ? {
          points: data.points,
          time: timeStr,
          gameId: data.gameId,
          name: data.name,
          sessionId: data.sessionId,
          userId: data.userId,
          organizationId: data.organizationId,
          ans: data.ans != null && String(data.ans).trim() !== '' ? String(data.ans) : '',
        }
      : {
          points: data.points,
          total_score: data.points,
          time: timeStr,
          gameId: data.gameId,
          name: data.name,
          sessionId: data.sessionId,
          userId: data.userId,
          organizationId: data.organizationId,
          ans: data.ans != null && String(data.ans).trim() !== '' ? String(data.ans) : '',
          ...(data.email != null && String(data.email).trim() !== '' ? { email: String(data.email).trim() } : {}),
          ...(data.user_type != null && String(data.user_type).trim() !== '' ? { user_type: String(data.user_type).trim() } : {}),
        };
    const guestUpdatePayload = {
      points: data.points,
      total_score: data.points,
      time: timeStr,
      reportId: rid,
    };
    const headers = {
      'client-secret': CLIENT_SECRET,
      'Content-Type': 'application/json',
      'Idempotency-Key': uuid4(),
    };
    const updateUrl = `${BASE_URL}/game/update/guest/score`;
    const addUrl = `${BASE_URL}/game/add/guest/score`;
    try {
      const response = await axios({
        method: hasIncomingRid ? 'put' : 'post',
        url: hasIncomingRid ? updateUrl : addUrl,
        headers,
        data: hasIncomingRid ? guestUpdatePayload : guestAddPayload,
      });
      return response.data;
    } catch (err) {
      const msg = String(err?.response?.data?.message || err?.message || '');
      const prismaCode = err?.response?.data?.err?.code;
      const isNotFoundUpdate =
        prismaCode === 'P2025' || /no record was found for an update/i.test(msg) || /not found/i.test(msg);
      if (!isNotFoundUpdate) throw err;

      // Guest update failed because row doesn't exist yet — retry as add.
      const retry = await axios({ method: 'post', url: addUrl, headers, data: guestAddPayload });
      return retry.data;
    }
  }

  // Registered user logic
  const isUpdate = Boolean(data.reportId);
  const url = isUpdate
    ? `${BASE_URL}/game-server/report/${data.reportId}`
    : `${BASE_URL}/game-server/report/add`;
  const method = isUpdate ? 'put' : 'post';

  let headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'Idempotency-Key': uuid4(),
  };

  try {
    const { token: _omitTokenInBody, ...bodyWithoutToken } = data || {};
    const registeredData = {
      ...bodyWithoutToken,
      points: data.points,
      total_score: data.points,
      time: guestTimeStringForApi(reportTimeSeconds),
    };
    if (!isUpdate) delete registeredData.reportId;
    const response = await axios({ method, url, headers, data: registeredData });
    return response.data;
  } catch (err) {
    if (err.response?.status === 401) {
      const newToken = await refreshToken(token);
      headers.Authorization = `Bearer ${newToken}`;
      const { token: _omitTokenInBody, ...bodyWithoutToken } = data || {};
      const registeredData = {
        ...bodyWithoutToken,
        points: data.points,
        total_score: data.points,
        time: guestTimeStringForApi(reportTimeSeconds),
      };
      if (!isUpdate) delete registeredData.reportId;
      const retry = await axios({ method, url, headers, data: registeredData });
      return retry.data;
    }
    throw err;
  }
}